import { SelectChangeEvent } from '@mui/material';
import { Box, Typography, useTheme } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import {
  useAuth,
  getAnnotationToolSupervisorPath,
  getAnnotationToolAdminSupervisorReviewPath,
  useAppDispatch,
  useAppSelector,
  STATUS_BACKGROUND_MAPPING,
  getStatusCode,
  type StatusText,
} from '@verantos-dev/core-annotation-tool';
import { useGetProjectsToManageQuery } from '@verantos-dev/feature-annotation-project-admin';
import { type DropdownListItem } from '@verantos-dev/shared-components';
import { OverflowTooltip } from '@verantos-dev/ui-annotation-tool';

import {
  useGetSupervisorBatchesQuery,
  useGetSupervisorProjectsQuery,
} from '../api/supervisorQueueApiSlice';
import { selectBatchId, selectProjectId } from '../state/selector';
import { clearBatchId, setBatchId, setProjectId } from '../state/supervisorQueueSlice';
import type { SupervisorTaskResponse } from '../types/responseTypes';

type DropdownChangeHandler = (
  event: SelectChangeEvent<unknown> | (Event & { target: { value: unknown; name: string } }),
  child: ReactNode
) => void;

export interface UseSupervisorQueueContentResult {
  projectOptions: DropdownListItem[];
  isProjectsLoading: boolean;
  batchOptions: DropdownListItem[];
  isBatchesLoading: boolean;
  handleProjectChange: DropdownChangeHandler;
  handleBatchChange: DropdownChangeHandler;
  columns: GridColDef<SupervisorTaskResponse>[];
  isAdminSupervisorForSelectedProject: boolean;
}

export const useSupervisorQueueContent = (): UseSupervisorQueueContentResult => {
  const dispatch = useAppDispatch();
  const projectId = useAppSelector(selectProjectId);
  const batchId = useAppSelector(selectBatchId);
  const { formatMessage } = useIntl();
  const { user } = useAuth();

  // All projects — for admins (reuses the existing manage-projects endpoint)
  const {
    data: adminProjectsData,
    isLoading: isAdminProjectsLoading,
    isFetching: isAdminProjectsFetching,
  } = useGetProjectsToManageQuery(undefined, { skip: !user.isAdmin });

  // Supervisor-assigned projects — only for users with ANNOTATION_USER role
  const {
    data: supervisorProjectsData,
    isLoading: isSupervisorProjectsLoading,
    isFetching: isSupervisorProjectsFetching,
  } = useGetSupervisorProjectsQuery(undefined, { skip: !user.isUser && !user.isAdmin });

  const {
    data: batchesData,
    isLoading: isBatchesLoading,
    isFetching: isBatchesFetching,
  } = useGetSupervisorBatchesQuery({ projectId: projectId as string }, { skip: !projectId });

  const isProjectsLoading = user.isAdmin
    ? isAdminProjectsLoading || isAdminProjectsFetching
    : isSupervisorProjectsLoading || isSupervisorProjectsFetching;

  // Admins: map ManageProjectResponse {projectId, projectName} → DropdownListItem
  // Supervisors: map QueueDropdownResponse {id, label} → DropdownListItem
  const projectOptions: DropdownListItem[] = useMemo(() => {
    if (user.isAdmin) {
      if (!adminProjectsData?.data?.length) return [];
      return adminProjectsData.data.map((project) => ({
        value: String(project.projectId),
        displayValue: project.projectName,
      }));
    }
    if (!supervisorProjectsData?.data?.length) return [];
    return supervisorProjectsData.data.map((project) => ({
      value: String(project.id),
      displayValue: project.label,
    }));
  }, [user.isAdmin, adminProjectsData, supervisorProjectsData]);

  // Set of project IDs where admin is also assigned as supervisor (for edit-mode routing)
  const supervisorProjectIds = useMemo<Set<string>>(() => {
    if (!user.isAdmin || !user.isUser) return new Set();
    return new Set(supervisorProjectsData?.data?.map((p) => String(p.id)) ?? []);
  }, [user.isAdmin, user.isUser, supervisorProjectsData]);

  // True when admin has selected a project they are also supervisor for
  const isAdminSupervisorForSelectedProject = useMemo(() => {
    if (!user.isAdmin || !projectId) return false;
    return supervisorProjectIds.has(projectId);
  }, [user.isAdmin, projectId, supervisorProjectIds]);

  const batchOptions: DropdownListItem[] = useMemo(() => {
    if (!batchesData?.data || batchesData.data.length === 0) {
      return [];
    }
    return batchesData.data.map((batch) => ({
      value: String(batch.id),
      displayValue: batch.label,
    }));
  }, [batchesData]);

  const handleProjectChange: DropdownChangeHandler = (event) => {
    const value = event.target?.value;
    if (typeof value === 'string') {
      dispatch(setProjectId(value));
      dispatch(clearBatchId());
    }
  };

  const handleBatchChange: DropdownChangeHandler = (event) => {
    const value = event.target?.value;
    if (typeof value === 'string') {
      dispatch(setBatchId(value));
    }
  };

  const theme = useTheme();
  const columns: GridColDef<SupervisorTaskResponse>[] = useMemo(
    () => [
      {
        field: 'encounterId',
        headerName: 'Encounter ID',
        minWidth: 216,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
          const encounterId = params?.row.encounterId;
          // Admin on a non-supervisor project → read-only route; everyone else → regular route
          const basePath =
            user.isAdmin && !isAdminSupervisorForSelectedProject
              ? getAnnotationToolAdminSupervisorReviewPath()
              : getAnnotationToolSupervisorPath();
          // batchId is carried via the URL (not just Redux) so it survives a page refresh
          const reviewPath = `${basePath}/review/${encounterId}?batchId=${encodeURIComponent(
            batchId ?? ''
          )}`;
          return (
            <OverflowTooltip title={encounterId ?? ''}>
              <Link
                to={reviewPath}
                style={{
                  textDecoration: 'none',
                  color: theme.palette.primary.main,
                  fontWeight: 'bold',
                }}
              >
                {encounterId}
              </Link>
            </OverflowTooltip>
          );
        },
      },
      {
        field: 'notesCount',
        headerName: '#Notes',
        minWidth: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'totalChars',
        headerName: 'Total characters',
        minWidth: 155,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'patientInfo',
        headerName: 'Patient ID/ Sex / Age',
        minWidth: 210,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value: any, row: SupervisorTaskResponse) => {
          if (!row) return '';
          const { patientId, patientSex, patientAge } = row;
          const parts = [
            patientId || '',
            patientSex || '',
            patientAge !== undefined && patientAge !== null ? String(patientAge) : '',
          ].filter(Boolean);
          return parts.join(' / ');
        },
      },
      {
        field: 'encounterType',
        headerName: 'Type',
        minWidth: 130,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value: any, row: SupervisorTaskResponse) => {
          return row?.encounterType ? row.encounterType.toLocaleLowerCase() : '';
        },
        renderCell: (params) => {
          const encounterType = params?.row.encounterType;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
              <Box
                sx={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '100%',
                  backgroundColor:
                    STATUS_BACKGROUND_MAPPING[getStatusCode(encounterType as StatusText)]
                      ?.backgroundColor,
                }}
              ></Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textTransform: 'capitalize' }}
              >
                {encounterType.toLocaleLowerCase()}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'encounterTimeInfo',
        headerName: 'Encounter start - Encounter end',
        minWidth: 310,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value: any, row: SupervisorTaskResponse) => {
          if (!row) return '';
          const { encounterStart, encounterEnd } = row;
          const parts = [
            dayjs(encounterStart).format('MM/DD/YYYY HH:mm') || '',
            dayjs(encounterEnd).format('MM/DD/YYYY HH:mm') || '',
          ].filter(Boolean);
          return parts.join(' - ');
        },
      },
      {
        field: 'annotator1Name',
        headerName: 'Annotator 1',
        minWidth: 155,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'annotator2Name',
        headerName: 'Annotator 2',
        minWidth: 155,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 210,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        sortComparator: (v1, v2) => {
          const priority: Record<string, number> = {
            [formatMessage({ id: 'supervisorQueue.content.table.status.conflicts_pending' })]: 0,
            [formatMessage({ id: 'supervisorQueue.content.table.status.pending' })]: 1,
            [formatMessage({ id: 'supervisorQueue.content.table.status.draft' })]: 2,
            [formatMessage({ id: 'supervisorQueue.content.table.status.finalized_a1' })]: 3,
            [formatMessage({ id: 'supervisorQueue.content.table.status.finalized_a2' })]: 4,
            [formatMessage({ id: 'supervisorQueue.content.table.status.completed' })]: 5,
          };

          return (priority[v1] ?? 99) - (priority[v2] ?? 99);
        },
        valueGetter: (value: any, row: SupervisorTaskResponse) => {
          if (!row?.status) return '';
          return formatMessage({
            id: `supervisorQueue.content.table.status.${row.status.toLocaleLowerCase()}`,
          });
        },
        renderCell: (params) => {
          const status = params?.row.status;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
              <Box
                sx={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '100%',
                  backgroundColor:
                    STATUS_BACKGROUND_MAPPING[getStatusCode(status as StatusText)]?.backgroundColor,
                }}
              ></Box>
              <Typography variant="body2" color="text.secondary">
                {formatMessage({
                  id: `supervisorQueue.content.table.status.${status.toLocaleLowerCase()}`,
                })}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'conflictsCount',
        headerName: '# Conflicts',
        minWidth: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => {
          const conflictsCount = params?.row.conflictsCount;
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(2),
                justifyContent: 'center',
                backgroundColor: conflictsCount > 0 ? theme.palette.error.main : '',
                px: theme.spacing(2),
                py: theme.spacing(1),
                borderRadius: theme.spacing(1),
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: conflictsCount > 0 ? theme.palette.error.contrastText : '',
                }}
              >
                {conflictsCount}
              </Typography>
            </Box>
          );
        },
      },
    ],
    [formatMessage, theme, user.isAdmin, isAdminSupervisorForSelectedProject, batchId]
  );

  return {
    projectOptions,
    isProjectsLoading,
    batchOptions,
    isBatchesLoading: isBatchesLoading || isBatchesFetching,
    handleProjectChange,
    handleBatchChange,
    columns,
    isAdminSupervisorForSelectedProject,
  };
};
